import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { browserBuild, useReleases } from "@/api/releases";
import { useListQueryState } from "@/lib/listQuery";
import { releaseNotes } from "@/lib/releaseState";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { SystemInformation } from "@/components/releases/SystemInformation";
export function ReleasesPage() {
  const { t, i18n } = useTranslation();
  const list = useListQueryState();
  const query = useReleases(list.page);
  const page = query.data;
  useEffect(() => {
    if (!page) return;
    const last = Math.max(1, Math.ceil(page.total / page.pageSize));
    if (list.page > last) list.updatePage(last);
  }, [page, list.page, list.updatePage]);
  return (
    <PageBody className="min-h-0 overflow-y-auto">
      <h1 className="text-xl font-semibold">{t("releases.title")}</h1>
      <p className="mt-1 text-sm text-ink-soft">{t("releases.description")}</p>
      <div className="mt-4">
        <SystemInformation showHistoryLink={false} />
      </div>
      {query.isLoading ? (
        <p className="mt-4">{t("common.loading")}</p>
      ) : query.error ? (
        <div role="alert" className="mt-4">
          {t("releases.loadError")}{" "}
          <Button variant="secondary" onClick={() => query.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : !page || page.total === 0 ? (
        <p className="mt-4 text-sm text-ink-soft">{t("releases.empty")}</p>
      ) : (
        <>
          <ol className="mt-5 space-y-4">
            {page.data.map((release) => {
              const notes = releaseNotes(release.notes, i18n.language);
              return (
                <li
                  key={release.id}
                  className="rounded-md border border-line bg-surface p-4"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-mono tabular text-base font-semibold">
                      {t("common.webVersion", { version: release.version })}
                    </h2>
                    <time className="text-xs text-ink-faint">
                      {new Date(release.deployedAt).toLocaleString(
                        i18n.language,
                      )}
                    </time>
                    {release.id === browserBuild.id && (
                      <span className="text-xs text-brand">
                        {t("releases.thisBrowser")}
                      </span>
                    )}
                    {release.id === page.current?.id && (
                      <span className="text-xs text-ok">
                        {t("releases.serving")}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 text-sm font-semibold">{notes.title}</h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-soft">
                    {notes.changes.map((change, index) => (
                      <li key={index}>{change}</li>
                    ))}
                  </ul>
                  {release.source === "verified_import" && (
                    <p className="mt-3 text-xs text-ink-faint">
                      {t("releases.imported")}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
          <nav
            aria-label={t("releases.pagination")}
            className="mt-4 flex items-center justify-between gap-3"
          >
            <Button
              variant="secondary"
              disabled={list.page <= 1}
              onClick={() => list.updatePage(list.page - 1)}
            >
              {t("releases.previous")}
            </Button>
            <span className="text-xs text-ink-faint">
              {t("releases.page", {
                page: list.page,
                pages: Math.max(1, Math.ceil(page.total / page.pageSize)),
              })}
            </span>
            <Button
              variant="secondary"
              disabled={list.page * page.pageSize >= page.total}
              onClick={() => list.updatePage(list.page + 1)}
            >
              {t("releases.next")}
            </Button>
          </nav>
        </>
      )}
    </PageBody>
  );
}
